import { Button, DateTimePicker, Modal, ModalFooter } from '@/components/common';
import { InstitutionBasicDTO, UserDTO } from '@/models';
import React, { useEffect, useMemo, useState } from 'react';

export interface RfidTagCreateAssignment {
  userId: string;
  expiresAt?: string;
}

export interface RfidTagCreateModalProps {
  isOpen: boolean;
  /** Prefilled institution when the user has only one option, or the current filter selection. */
  defaultInstitutionId: string;
  institutions: InstitutionBasicDTO[];
  users: UserDTO[];
  isLoadingUsers: boolean;
  isSaving: boolean;
  onConfirm: (
    institutionId: string,
    serialNumber: string,
    assignment: RfidTagCreateAssignment | null
  ) => Promise<void> | void;
  onClose: () => void;
}

export const RfidTagCreateModal: React.FC<RfidTagCreateModalProps> = ({
  isOpen,
  defaultInstitutionId,
  institutions,
  users,
  isLoadingUsers,
  isSaving,
  onConfirm,
  onClose,
}) => {
  const [institutionId, setInstitutionId] = useState(defaultInstitutionId);
  const [serialNumber, setSerialNumber] = useState('');
  const [assignEnabled, setAssignEnabled] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState('');
  const [expiresAt, setExpiresAt] = useState<Date | null>(null);
  const [userSearch, setUserSearch] = useState('');

  // Reset on open.
  useEffect(() => {
    if (isOpen) {
      setInstitutionId(defaultInstitutionId);
      setSerialNumber('');
      setAssignEnabled(false);
      setSelectedUserId('');
      setExpiresAt(null);
      setUserSearch('');
    }
  }, [isOpen, defaultInstitutionId]);

  // Scope users to the chosen institution.
  const institutionUsers = useMemo(() => {
    if (!institutionId) return [];
    return users.filter((u) => u.institutionId === institutionId);
  }, [users, institutionId]);

  const filteredUsers = useMemo(() => {
    const q = userSearch.trim().toLowerCase();
    if (!q) return institutionUsers;
    return institutionUsers.filter((u) => {
      const name = `${u.firstName ?? ''} ${u.lastName ?? ''}`.trim().toLowerCase();
      const email = (u.email ?? '').toLowerCase();
      return name.includes(q) || email.includes(q);
    });
  }, [institutionUsers, userSearch]);

  // Reset selected user when institution changes — it might no longer belong to the institution.
  useEffect(() => {
    setSelectedUserId('');
  }, [institutionId]);

  const trimmedSerial = serialNumber.trim();
  const canConfirm =
    !!institutionId &&
    trimmedSerial.length > 0 &&
    (!assignEnabled || !!selectedUserId) &&
    !isSaving;

  const handleConfirm = async () => {
    if (!canConfirm) return;
    const assignment: RfidTagCreateAssignment | null =
      assignEnabled && selectedUserId
        ? {
            userId: selectedUserId,
            expiresAt: expiresAt ? expiresAt.toISOString() : undefined,
          }
        : null;
    await onConfirm(institutionId, trimmedSerial, assignment);
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      closeOnBackdropClick={false}
      title="Create RFID tag"
      size="md"
      footer={
        <ModalFooter
          onCancel={onClose}
          onConfirm={handleConfirm}
          confirmText="Create"
          isLoading={isSaving}
          disabled={!canConfirm}
        />
      }
    >
      <div className="mb-3">
        <label htmlFor="rfid-create-institution" className="form-label">
          Institution <span className="text-danger">*</span>
        </label>
        <select
          id="rfid-create-institution"
          className="form-select"
          value={institutionId}
          onChange={(e) => setInstitutionId(e.target.value)}
        >
          <option value="">Select institution...</option>
          {institutions.map((t) => (
            <option key={t.id} value={t.id}>
              {t.name}
            </option>
          ))}
        </select>
      </div>

      <div className="mb-3">
        <label htmlFor="rfid-create-serial" className="form-label">
          Serial number <span className="text-danger">*</span>
        </label>
        <input
          id="rfid-create-serial"
          type="text"
          className="form-control"
          placeholder="e.g. RF-ACME-003"
          value={serialNumber}
          onChange={(e) => setSerialNumber(e.target.value)}
          autoFocus
        />
      </div>

      <div className="form-check form-switch mb-3">
        <input
          id="rfid-create-assign-toggle"
          type="checkbox"
          role="switch"
          className="form-check-input"
          checked={assignEnabled}
          onChange={(e) => setAssignEnabled(e.target.checked)}
        />
        <label htmlFor="rfid-create-assign-toggle" className="form-check-label">
          Assign to a user now
        </label>
      </div>

      {assignEnabled && (
        <>
          <div className="mb-3">
            <label htmlFor="rfid-create-user-search" className="form-label">
              Search user
            </label>
            <input
              id="rfid-create-user-search"
              type="text"
              className="form-control form-control-sm"
              placeholder="Name or email..."
              value={userSearch}
              onChange={(e) => setUserSearch(e.target.value)}
            />
          </div>

          <div className="mb-3">
            <label htmlFor="rfid-create-user" className="form-label">
              User <span className="text-danger">*</span>
            </label>
            <select
              id="rfid-create-user"
              className="form-select"
              value={selectedUserId}
              onChange={(e) => setSelectedUserId(e.target.value)}
              disabled={isLoadingUsers || filteredUsers.length === 0}
              size={Math.min(8, Math.max(4, filteredUsers.length))}
            >
              {filteredUsers.length === 0 && !isLoadingUsers && (
                <option value="" disabled>
                  No users found
                </option>
              )}
              {filteredUsers.map((u) => {
                const fullName = `${u.firstName ?? ''} ${u.lastName ?? ''}`.trim();
                const label = fullName ? `${fullName} (${u.email ?? '—'})` : (u.email ?? u.id);
                return (
                  <option key={u.id} value={u.id}>
                    {label}
                  </option>
                );
              })}
            </select>
            {isLoadingUsers && <small className="text-muted">Loading users...</small>}
          </div>

          <div className="mb-3">
            <label className="form-label">Expires at (optional)</label>
            <DateTimePicker value={expiresAt} onChange={setExpiresAt} size="sm" />
            {expiresAt && (
              <Button
                variant="link"
                size="sm"
                className="p-0 mt-1"
                onClick={() => setExpiresAt(null)}
              >
                Clear
              </Button>
            )}
          </div>
        </>
      )}
    </Modal>
  );
};

export default RfidTagCreateModal;
